import '../../../../css/business-modal.css';
import React, { useState, useEffect } from 'react';
import { editUserProfile } from '../../../../services/userService';
import { cropAndResizeProfileImage } from '../../../../utils/imageUtils';
import { US_STATES } from '../../../../utils/usStates';
import { useAuth } from '../../../../context/AuthContext';
import { getImage } from '../../../../utils/cloudinarySizing';

const BusinessProfileModal = ({ data, onClose, handleUpdate }) => {
    const [formData, setFormData] = useState({
        image: '',
        rawImage: null,
        business_name: '',
        city: '',
        state: '',
        services: '',
        website: '',
        instagram: '',
        facebook: '',
        youtube: '',
        x: '',
    });

    const [previewPhoto, setPreviewPhoto] = useState(null);

    const { setProfileThumbnail } = useAuth();

    const handleChange = (e, type) => {
        if (type === 'image') {
            const file = e.target.files[0];
            if (file) {
                setFormData({
                    ...formData,
                    image: URL.createObjectURL(file), // preview
                    rawImage: file, // keep original for upload
                });

                cropAndResizeProfileImage(file, (processedBase64) => {
                    setPreviewPhoto(processedBase64);
                });
            }
        } else {
            const { name, value } = e.target;
            setFormData({ ...formData, [name]: value });
        }
    };

    const normalizeUrl = (url) => {
        if (!url) return "";

        let trimmed = url.trim();

        // If starts with http already → leave it
        if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) {
            return trimmed;
        }

        // If starts with www → add https://
        if (trimmed.startsWith("www.")) {
            return `https://${trimmed}`;
        }

        // If contains a dot "mybiz.com" → assume domain
        if (trimmed.includes(".")) {
            return `https://${trimmed}`;
        }

        // Otherwise return as-is (user typed something random)
        return trimmed;
    };

    const handleSubmit = async () => {
        try {
            const profileForm = new FormData();
            profileForm.append('business_name', formData.business_name);
            profileForm.append('city', formData.city);
            profileForm.append('state', formData.state);
            profileForm.append('website', normalizeUrl(formData.website));
            profileForm.append('instagram', normalizeUrl(formData.instagram));
            profileForm.append('facebook', normalizeUrl(formData.facebook));
            profileForm.append('youtube', normalizeUrl(formData.youtube));
            profileForm.append('x', normalizeUrl(formData.x));

            if (formData.rawImage) {
                profileForm.append('photo_original', formData.rawImage);
            }

            // Send to backend
            const updatedProfile = await editUserProfile(profileForm);

            if (updatedProfile.photo_original) {
                setProfileThumbnail(updatedProfile.photo_original);
                localStorage.setItem("avatar", updatedProfile.photo_original);
            }

            // ⭐ NEW — Send ONLY backend fields to parent.
            // No spreading old stale data. No merging.
            handleUpdate({
                id: data.id,
                business_name: updatedProfile.business?.business_name || "",
                website: updatedProfile.business?.website || "",
                instagram: updatedProfile.business?.instagram || "",
                facebook: updatedProfile.business?.facebook || "",
                youtube: updatedProfile.business?.youtube || "",
                x: updatedProfile.business?.x || "",
                profile: {
                    city: updatedProfile.city || "",
                    state: updatedProfile.state || "",
                    photo_original: updatedProfile.photo_original || null,
                }
            });

            onClose();
            alert("Profile updated successfully!");

        } catch (error) {
            console.error("Profile update failed:", error);
            alert("Failed to update profile");
        }
    };

    useEffect(() => {
        if (data) {
            setFormData({
                image: data.profile.photo_original || '/servspace-business-placeholder.png',
                rawImage: null, // keep original file for upload
                business_name: data.business_name || '',
                city: data.profile.city || '',
                state: data.profile.state || '',
                services: data.services || '',
                website: data.website || '',
                instagram: data.instagram || '',
                facebook: data.facebook || '',
                youtube: data.youtube || '',
                x: data.x || '',
            });
        }
    }, [data]);

    return (
        <div
            className="business-modal-backdrop"
            onMouseDown={(e) => {
                // If they clicked *directly on the backdrop* (not inside modal)
                // we mark it so mouseup can close it.
                if (e.target === e.currentTarget) {
                    e.currentTarget.dataset.shouldClose = "true";
                }
            }}
            onMouseUp={(e) => {
                // Only close if mouse started AND ended on the backdrop
                if (e.currentTarget.dataset.shouldClose === "true" && e.target === e.currentTarget) {
                    onClose();
                }
                e.currentTarget.dataset.shouldClose = "false";
            }}
        >
            <div className="business-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="business-modal-scroll">
                    <h3 className='business-modal-title'>Edit Profile</h3>

                    {/* Profile Picture */}
                    <div className="form-group">
                        <label>Profile Photo:</label>
                        <div className="profile-preview-container">
                            <img
                                src={previewPhoto || getImage(formData.image, "avatar_md") || 'servspace-business-placeholder.png'}
                                alt="Profile"
                                className="profile-image-preview"
                            />
                        </div>
                        <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleChange(e, 'image')}
                        />
                    </div>

                    <div className="form-group">
                        <label>Business Name:</label>
                        <input
                            type="text"
                            name="business_name"
                            value={formData.business_name}
                            onChange={handleChange}
                            placeholder="First Name"
                        />
                    </div>

                    <div className="name-fields-container">
                        <div className="form-group">
                            <label>City / Town:</label>
                            <input
                                type="text"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                placeholder="City"
                            />
                        </div>

                        <div className="form-group">
                            <label>State:</label>
                            <select
                                name="state"
                                value={formData.state}
                                onChange={handleChange}
                            >
                                <option value="">Select</option>
                                {US_STATES.map((state) => (
                                    <option key={state.value} value={state.value}>
                                        {state.value}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>


                    {/* Portfolio Link */}
                    <div className="form-group">
                        <label><i className="fa-solid fa-link"></i> Website:</label>
                        <input
                            type="text"
                            name="website"
                            value={formData.website}
                            onChange={handleChange}
                            placeholder="Website"
                        />
                    </div>

                    {/* Social Links */}
                    <div className="form-group">
                        <label><i className="fa-brands fa-instagram"></i> Instagram URL:</label>
                        <input
                            type="text"
                            name="instagram"
                            value={formData.instagram}
                            onChange={handleChange}
                            placeholder="Instagram URL"
                        />
                    </div>
                    <div className="form-group">
                        <label><i className="fa-brands fa-facebook"></i> Facebook URL:</label>
                        <input
                            type="text"
                            name="facebook"
                            value={formData.facebook}
                            onChange={handleChange}
                            placeholder="Facebook URL"
                        />
                    </div>
                    <div className="form-group">
                        <label><i className="fa-brands fa-youtube"></i> YouTube URL:</label>
                        <input
                            type="text"
                            name="youtube"
                            value={formData.youtube}
                            onChange={handleChange}
                            placeholder="YouTube URL"
                        />
                    </div>
                    <div className="form-group">
                        <label><i className="fa-brands fa-x-twitter"></i> X (Twitter) URL:</label>
                        <input
                            type="text"
                            name="x"
                            value={formData.x}
                            onChange={handleChange}
                            placeholder="X (Twitter) URL"
                        />
                    </div>

                    {/* Buttons */}
                    <div className="business-modal-buttons">
                        <button className='save-btn' onClick={handleSubmit}>Save</button>
                        <button className='cancel-btn' onClick={onClose}>Cancel</button>
                    </div>
                </div>
            </div>
        </div>

    );
};

export default BusinessProfileModal;
